import { default as Space } from 'antd/es/space';
import TreeSelect from 'antd/es/tree-select';
import dayjs from 'dayjs';
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { formatDate } from 'utils/dayjs';
import { hasValue } from 'utils/hasValue';
import { normalize } from 'utils/normalize';

import { useGetCollectionStatsOrdersStatusCountsReports } from 'api';
import type {
  CollectionCountStatusReportsDataItem,
  GetCollectionStatsOrdersStatusCountsReportsParams
} from 'api';

import { useOrganisationId } from 'hooks/context';
import { useGlobalFilters } from 'hooks/useGlobalFilters';

import { TertiaryButton } from '@atoms/buttons/TertiaryButton';

import { LocalDateRangeWrapper } from '@molecules/LocalDateRangeWrapper';
import { SelectVirtualAccount } from '@molecules/SelectVirtualAccount';

import {
  COLLECTIONS_ACCOUNTS_FILTER_TEST_ID,
  COLLECTIONS_FILTERS_RESET_TEST_ID,
  COLLECTIONS_STATUS_FILTER_TEST_ID
} from './constants';
import type { Action, FiltersState } from './localState';

type Props = {
  filters: FiltersState;
  dispatch: React.Dispatch<Action>;
  updateAccountNumber: (accountNumber?: string) => Action;
  updateReasonCode: (value: { status?: string; reasonCode?: number }) => Action;
  resetState: () => Action;
};

const STATUS_OR_REASON_CODE_OPTIONS = {
  ALL: 'All'
} as const;

export const CollectionOrdersFilters = ({
  filters,
  dispatch,
  resetState,
  updateAccountNumber,
  updateReasonCode
}: Props) => {
  const hasFilters = useMemo(() => {
    return Object.values(filters).some(
      (value) => Boolean(value) && value !== STATUS_OR_REASON_CODE_OPTIONS.ALL
    );
  }, [filters]);

  const { filters: globalFilters } = useGlobalFilters();

  const [reasonCodes, setReasonCodes] = useState<
    CollectionCountStatusReportsDataItem[]
  >([]);

  const organisationId = useOrganisationId<number>();
  const [statusOrReasonCode, setStatusOrReasonCode] = useState<string>(
    STATUS_OR_REASON_CODE_OPTIONS.ALL
  );

  const dateRange =
    useMemo<GetCollectionStatsOrdersStatusCountsReportsParams>(() => {
      const queryParams: GetCollectionStatsOrdersStatusCountsReportsParams = {
        created_after: dayjs().subtract(30, 'day').format('YYYY-MM-DD')
      };
      if (globalFilters?.dateRange?.from) {
        queryParams.created_after = formatDate(
          globalFilters.dateRange.from,
          'utc',
          'iso'
        );
      }
      if (globalFilters?.dateRange?.to) {
        queryParams.created_before = formatDate(
          globalFilters.dateRange.to,
          'utc',
          'iso'
        );
      }
      return queryParams;
    }, [globalFilters.dateRange]);

  const { data: _reasonCodes, isPending } =
    useGetCollectionStatsOrdersStatusCountsReports(organisationId, dateRange);

  useEffect(() => {
    const incoming = _reasonCodes?.data;
    if (!incoming?.length) return;

    setReasonCodes((prev) => {
      // merge reason statuses by `code`
      const mergedStatuses = _.unionBy(prev, incoming, 'code');

      // ensure nested items (reason codes) are also merged per status
      return mergedStatuses.map((status) => {
        const prevStatus = prev.find((p) => p.code === status.code);
        const incomingStatus = incoming.find((n) => n.code === status.code);

        // merge `items` arrays, deduping by `id`
        const mergedItems = _.unionBy(
          prevStatus?.items || [],
          incomingStatus?.items || [],
          'id'
        );

        return { ...status, items: mergedItems };
      });
    });
  }, [_reasonCodes?.data]);

  const treeData = useMemo(() => {
    if (!hasValue(reasonCodes)) return [];
    return mapToTreeData(reasonCodes);
  }, [reasonCodes]);

  const handleSelectStatusOrReasonCodeSelect = useCallback(
    (node: ReasonCodeNodeValue) => {
      if (node.value === STATUS_OR_REASON_CODE_OPTIONS.ALL) {
        dispatch(
          updateReasonCode({
            status: undefined,
            reasonCode: undefined
          })
        );
      }
      if (statusOrReasonCode === node.value) {
        return;
      }
      if (isReasonCodeNodeValue(node)) {
        dispatch(
          updateReasonCode({
            status: node.status
          })
        );
      }

      if (isReasonCodeChild(node)) {
        dispatch(
          updateReasonCode({
            status: node.parentStatus,
            reasonCode: node.reasonCode
          })
        );
      }
      setStatusOrReasonCode(node.value);
    },
    [dispatch, statusOrReasonCode, updateReasonCode]
  );

  return (
    <div className="flex items-center">
      <Space wrap size="small">
        <LocalDateRangeWrapper />
        <SelectVirtualAccount
          onChangeCallback={(accountNumber) => {
            dispatch(
              updateAccountNumber(
                accountNumber === STATUS_OR_REASON_CODE_OPTIONS.ALL
                  ? STATUS_OR_REASON_CODE_OPTIONS.ALL
                  : accountNumber
              )
            );
          }}
          currentAccountNumber={filters.accountNumber}
          e2eId={COLLECTIONS_ACCOUNTS_FILTER_TEST_ID}
          filterCryptoAccounts={true}
        />

        <TreeSelect
          className="w-40"
          showSearch
          loading={isPending}
          filterTreeNode={(input, node) => {
            return normalize(node.title as string).includes(normalize(input));
          }}
          data-testid={COLLECTIONS_STATUS_FILTER_TEST_ID}
          value={statusOrReasonCode}
          popupMatchSelectWidth={false}
          treeData={treeData}
          placeholder="Please select"
          onSelect={(_, node) => {
            handleSelectStatusOrReasonCodeSelect(node);
          }}
        />

        {hasFilters && (
          <TertiaryButton
            data-testid={COLLECTIONS_FILTERS_RESET_TEST_ID}
            className="h-10 font-semibold"
            onClick={() => {
              setStatusOrReasonCode(STATUS_OR_REASON_CODE_OPTIONS.ALL);
              dispatch(resetState());
            }}
          >
            Clear filters
          </TertiaryButton>
        )}
      </Space>
    </div>
  );
};

function mapToTreeData(
  data: CollectionCountStatusReportsDataItem[]
): ReasonCodeNodeValue[] {
  if (!data) return [];
  // ALL is always included
  return [
    {
      title: 'All Statuses',
      value: STATUS_OR_REASON_CODE_OPTIONS.ALL,
      status: undefined
    },
    // include the status codes
    ...data.map((node) => {
      return {
        title: node.name,
        value: node.code,
        status: node.code,
        /**
         * note, if you have a legacy reason code with name = 'Compliance check' and a newer reason code with the same name, you can end up with duplicates.
         * We're aware of this issue and will add a deprecated tag provided we get the list of all deprecated reason codes.
         */
        // include the reason codes
        children: node?.items?.map((item) => {
          return {
            title: item.name,
            value: `${node.code}-${item.id}`,
            reasonCode: item.id,
            parentStatus: node.code
          };
        })
      };
    })
  ];
}

function isReasonCodeNodeValue(
  obj: ReasonCodeNodeValue | ReasonCodeChild
): obj is ReasonCodeNodeValue {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    'children' in obj &&
    'status' in obj
  );
}

function isReasonCodeChild(
  obj: ReasonCodeNodeValue | ReasonCodeChild
): obj is ReasonCodeChild {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    'reasonCode' in obj &&
    'parentStatus' in obj
  );
}

interface ReasonCodeNodeValue {
  title: string;
  value: string;
  status?: string;
  children?: ReasonCodeChild[];
}

interface ReasonCodeChild {
  title: string;
  value: string;
  reasonCode: number;
  parentStatus: string;
}
